import { ArrowUp, Check } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";

import { cn } from "@/lib/utils";

import { Shell } from "./shell";

export function WizardStep({
	step,
	total,
	label,
	hint,
	error,
	onBack,
	children,
	footer,
	brand,
}: {
	step: number;
	total: number;
	label: string;
	hint?: string;
	error: string | null;
	onBack: () => void;
	children: ReactNode;
	footer?: ReactNode;
	brand?: ReactNode;
}) {
	return (
		<Shell align="top">
			<div className="absolute inset-x-0 top-0 h-1.5 bg-secondary">
				<div
					className="h-full bg-info transition-all duration-500"
					style={{ width: `${((step + 1) / total) * 100}%` }}
				/>
			</div>

			{!!brand && <div className="absolute inset-x-0 top-5 flex justify-center">{brand}</div>}

			{step > 0 && (
				<button
					type="button"
					onClick={onBack}
					className="absolute left-6 top-6 -m-3 flex items-center gap-2 p-3 text-base text-muted-foreground hover:text-foreground sm:-m-0 sm:gap-1 sm:p-0 sm:text-sm"
				>
					<ArrowUp className="size-4" />
					voltar
				</button>
			)}

			<div key={step} className="fade-up w-full max-w-2xl">
				<div className="mb-4 flex items-center gap-3 font-mono text-info">
					<span className="text-lg">
						{step + 1}
						<span className="text-muted-foreground">/{total}</span>
					</span>
				</div>

				<h2 className="font-display text-4xl leading-tight text-foreground sm:text-5xl">{label}</h2>

				{!!hint && <p className="mt-3 text-lg text-muted-foreground">{hint}</p>}

				<div className="mt-8">{children}</div>

				{!!error && <p className="mt-4 text-lg text-destructive">{error}</p>}

				{footer}
			</div>
		</Shell>
	);
}

export function WizardOk({ onClick }: { onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-lg font-semibold text-primary-foreground shadow hover:bg-primary-hover"
		>
			OK
			<Check className="size-4" />
		</button>
	);
}

export function ChoiceButton({
	active,
	onClick,
	children,
	className,
}: {
	active: boolean;
	onClick: () => void;
	children: ReactNode;
	className?: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"w-full rounded-xl border-2 px-8 py-5 text-left text-xl font-medium transition sm:w-auto sm:min-w-48",
				active
					? "border-info bg-info/10 text-foreground"
					: "border-foreground/15 bg-card text-foreground hover:border-info/60 hover:bg-info/5",
				className,
			)}
		>
			{children}
		</button>
	);
}

export function WizardInput({
	inputRef,
	value,
	inputMode,
	onChange,
	onEnter,
}: {
	inputRef: Ref<HTMLInputElement>;
	value: string;
	inputMode: ComponentProps<"input">["inputMode"];
	onChange: (value: string) => void;
	onEnter: () => void;
}) {
	return (
		<input
			ref={inputRef}
			value={value}
			inputMode={inputMode}
			onChange={(e) => onChange(e.target.value)}
			onKeyDown={(e) => e.key === "Enter" && onEnter()}
			className="w-full border-b-2 border-foreground/20 bg-transparent pb-3 font-display text-2xl text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:text-4xl"
		/>
	);
}
